import { parseQueryStatNumber } from "./queryBuilder";
import { scoreboardNameFromPlayer } from "./playerDisplayName";

const RATE_LEADERBOARDS = new Set([
  "batting-average",
  "on-base-percentage",
  "slugging-average",
  "fielding-percentage",
]);

const ERA_LEADERBOARDS = new Set(["era"]);
const DECIMAL_LEADERBOARDS = new Set(["range-factor"]);

const firstNumber = (...values) => {
  for (const value of values) {
    const num = parseQueryStatNumber(value);
    if (num != null) return num;
  }
  return 0;
};

export const getLeaderboardStatFormat = (leaderboardType) => {
  if (RATE_LEADERBOARDS.has(leaderboardType)) return "rate";
  if (ERA_LEADERBOARDS.has(leaderboardType)) return "era";
  if (DECIMAL_LEADERBOARDS.has(leaderboardType)) return "decimal2";
  return "count";
};

export const getLeaderboardStatValue = (player, leaderboardType) => {
  switch (leaderboardType) {
    case "home-runs":
      return firstNumber(player.homeruns, player.homeruns_num);
    case "batting-average":
      return firstNumber(player.battingAverage, player.battingAverage_num);
    case "rbi":
      return firstNumber(player.rbis, player.rbi, player.rbi_num, player.runBattedIn);
    case "hits":
      return firstNumber(player.hits, player.hits_num);
    case "on-base-percentage":
      return firstNumber(player.onBasePercentage, player.onBasePercentage_num);
    case "slugging-average":
      return firstNumber(player.sluggingAverage, player.sluggingAverage_num);
    case "runs":
      return firstNumber(player.runs, player.runs_num);
    case "doubles":
      return firstNumber(player.doubles, player.doubles_num);
    case "triples":
      return firstNumber(player.triples, player.triples_num);
    case "walks":
      return firstNumber(player.baseOnBalls, player.walks, player.walks_num);
    case "strikeouts":
      return firstNumber(player.strikeOut, player.strikeouts, player.strikeouts_num);
    case "wins":
      return firstNumber(player.wins, player.wins_num);
    case "era":
      return firstNumber(player.era, player.era_num);
    case "pitching-strikeouts":
      return firstNumber(
        player.strikeOuts,
        player.pitchingStrikeouts,
        player.pitchingStrikeouts_num
      );
    case "pitching-walks":
      return firstNumber(
        player.baseOnBalls,
        player.pitchingWalks,
        player.pitchingWalks_num
      );
    case "hits-allowed":
      return firstNumber(player.hits, player.hitsAllowed, player.hitsAllowed_num);
    case "earned-runs":
      return firstNumber(player.earnedRuns, player.earnedRuns_num);
    case "complete-games":
      return firstNumber(player.completeGames, player.completeGames_num);
    case "shutouts":
      return firstNumber(player.shutouts, player.shutouts_num);
    case "saves":
      return firstNumber(player.saves, player.saves_num);
    case "innings-pitched":
      return firstNumber(player.inningsPitched, player.inningsPitched_num);
    case "home-runs-allowed":
      return firstNumber(
        player.homeRuns,
        player.homeRunsAllowed,
        player.homeRunsAllowed_num
      );
    case "fielding-games":
      return firstNumber(player.g, player.games, player.games_num);
    case "chances":
      return firstNumber(player.ch, player.chances, player.chances_num);
    case "putouts":
      return firstNumber(player.po, player.putouts, player.putouts_num);
    case "assists":
      return firstNumber(player.a, player.assists, player.assists_num);
    case "errors":
      return firstNumber(player.e, player.errors, player.errors_num);
    case "fielding-percentage":
      return firstNumber(
        player["FLD%"],
        player.fieldingPercentage,
        player.fieldingPercentage_num
      );
    case "range-factor":
      return firstNumber(player.rf, player.rangeFactor, player.rangeFactor_num);
    default:
      return 0;
  }
};

export const getLeaderboardPlayerName = (player) =>
  scoreboardNameFromPlayer(player, "");

export const buildLeaderboardRows = (
  players = [],
  leaderboardType,
  { page = 0, pageSize = 25 } = {}
) =>
  players.map((player, index) => ({
    id: player.playerId || player._id || `row-${index}`,
    rank: page * pageSize + index + 1,
    playerId: player.playerId,
    playerName: getLeaderboardPlayerName(player),
    statValue: getLeaderboardStatValue(player, leaderboardType),
    bats: player.bats || player.bio?.bats || "-",
    throws: player.throws || player.bio?.throws || "-",
    bornIn: player.birthPlace || player.bio?.bornIn || player.bornIn || "-",
  }));
